import type {
  ChartSyllable,
  ChartTone,
} from "../content/pinyin-chart/pinyin-chart.js";
import { contourDrawing, toneDescriptions } from "./pinyin-chart-tones.js";

/** The ordinal the panel reads a tone out with. */
const ordinals = ["", "first", "second", "third", "fourth"];

/** Make an element with a class and, optionally, its text. */
function element<K extends keyof HTMLElementTagNameMap>(
  document: Document,
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const made = document.createElement(tag);

  if (className !== "") {
    made.className = className;
  }

  if (text !== undefined) {
    made.textContent = text;
  }

  return made;
}

/** Where a character's 词典 entry is. */
export function entryHref(hanzi: string): string {
  return `/cidian/${encodeURIComponent(hanzi)}/`;
}

/** One tone, as a card in the panel. */
function toneCard(document: Document, tone: ChartTone): HTMLLIElement {
  const description = toneDescriptions.find(
    (candidate) => candidate.tone === tone.tone,
  );
  const card = element(document, "li", "cb-pc-tone");

  card.dataset["tone"] = String(tone.tone);

  const head = element(document, "div", "cb-pc-tone-head");
  const reading = element(document, "span", "cb-pc-tone-reading");
  const pinyin = element(
    document,
    "span",
    `py-tone-${String(tone.tone)} cb-pc-tone-py`,
    tone.pinyin,
  );
  const bopomofo = element(document, "span", "cb-pc-tone-bpmf", tone.bopomofo);

  pinyin.lang = "zh-Latn-CN-pinyin";
  bopomofo.lang = "zh-Bopo";
  reading.append(pinyin, bopomofo);

  if (tone.audio === undefined) {
    head.append(reading);
  } else {
    const play = element(document, "button", "cb-pc-tone-play");

    play.type = "button";
    play.dataset["cbPcPlayTone"] = String(tone.tone);
    play.setAttribute(
      "aria-label",
      `Play ${tone.pinyin}, ${ordinals[tone.tone] ?? ""} tone`,
    );
    play.append(element(document, "span", "cb-pc-play-icon"), reading);
    head.append(play);
  }

  card.append(head);

  if (description !== undefined) {
    const name = element(document, "p", "cb-pc-tone-name");

    name.append(
      contourDrawing(document, description.contour),
      element(
        document,
        "span",
        "cb-pc-tone-name-text",
        `${description.name}, ${description.shape}`,
      ),
    );
    card.append(name);
  }

  if (tone.audio === undefined) {
    card.append(
      element(document, "p", "cb-pc-tone-missing", "No recording available"),
    );
  }

  if (tone.examples.length > 0) {
    const examples = element(document, "ul", "cb-pc-examples");

    for (const example of tone.examples) {
      const item = element(document, "li", "");
      const link = element(document, "a", "cb-pc-example");
      const hanzi = element(document, "span", "cb-pc-hanzi", example.hanzi);

      hanzi.lang = "zh-Hans";
      link.href = entryHref(example.hanzi);
      link.append(hanzi);

      if (example.gloss !== "") {
        link.append(element(document, "span", "cb-pc-gloss", example.gloss));
      }

      if (example.band !== undefined) {
        link.append(
          element(document, "span", "cb-pc-band", `HSK ${example.band}`),
        );
      }

      item.append(link);
      examples.append(item);
    }

    card.append(examples);
  }

  if (tone.page !== undefined) {
    const more = element(
      document,
      "a",
      "cb-pc-tone-more",
      `Words pronounced ${tone.pinyin}`,
    );

    more.href = tone.page;
    card.append(more);
  }

  return card;
}

/**
 * Draw a syllable into the panel under the grid.
 *
 * Built with the DOM rather than with markup in a string, because the glosses
 * are CC-CEDICT's English and a gloss holding `<` would otherwise be markup.
 * @param panel - The panel to fill. Whatever it held is replaced.
 * @param syllable - The syllable to draw.
 */
export function drawPanel(panel: HTMLElement, syllable: ChartSyllable): void {
  const document = panel.ownerDocument;
  const head = element(document, "header", "cb-pc-panel-head");
  const heading = element(document, "h2", "cb-pc-panel-title");
  const pinyin = element(document, "span", "cb-pc-panel-py", syllable.pinyin);
  const bopomofo = element(
    document,
    "span",
    "cb-pc-panel-bpmf",
    syllable.bopomofo,
  );

  heading.id = "cb-pc-panel-heading";
  pinyin.lang = "zh-Latn-CN-pinyin";
  bopomofo.lang = "zh-Bopo";
  heading.append(pinyin, bopomofo);

  const facts = element(document, "dl", "cb-pc-facts");
  const parts: readonly (readonly [string, string])[] = [
    ["Initial", syllable.initial === "" ? "None" : syllable.initial],
    ["Final", syllable.final],
    ["Pronunciation (IPA)", syllable.ipa],
    ["Wade-Giles spelling", syllable.wadeGiles],
  ];

  for (const [term, value] of parts) {
    const pair = element(document, "div", "cb-pc-fact");

    pair.append(
      element(document, "dt", "", term),
      element(document, "dd", "", value),
    );
    facts.append(pair);
  }

  const actions = element(document, "div", "cb-pc-panel-actions");

  if (syllable.tones.some((tone) => tone.audio !== undefined)) {
    const all = element(
      document,
      "button",
      "cb-tool-button cb-tool-button-primary cb-pc-play-all",
      "Play recorded tones",
    );

    all.type = "button";
    all.dataset["cbPcPlayAll"] = "";
    actions.append(all);
  }

  if (syllable.hub !== undefined) {
    const hub = element(
      document,
      "a",
      "cb-pc-hub",
      `Words pronounced ${syllable.pinyin} in any tone`,
    );

    hub.href = syllable.hub;
    actions.append(hub);
  }

  head.append(heading, facts, actions);

  const children: Node[] = [head];

  if (syllable.notes.length > 0) {
    const notes = element(document, "ul", "cb-pc-notes");

    for (const note of syllable.notes) {
      notes.append(element(document, "li", "", note));
    }

    children.push(notes);
  }

  const tones = element(document, "ol", "cb-pc-tones");

  for (const tone of syllable.tones) {
    tones.append(toneCard(document, tone));
  }

  if (syllable.neutral !== undefined) {
    const card = element(document, "li", "cb-pc-tone is-neutral");
    const play = element(document, "button", "cb-pc-tone-play");
    const reading = element(document, "span", "cb-pc-tone-reading");
    const pinyin = element(
      document,
      "span",
      "py-tone-5 cb-pc-tone-py",
      syllable.neutral.pinyin,
    );
    const word = element(document, "span", "cb-pc-neutral-word");
    const hanzi = element(document, "span", "", syllable.neutral.word);

    hanzi.lang = "zh-Hans";
    word.append("in ", hanzi);
    reading.append(pinyin, word);
    play.type = "button";
    play.dataset["cbPcPlayNeutral"] = "";
    play.setAttribute(
      "aria-label",
      `Play ${syllable.neutral.pinyin}, neutral tone, in ${syllable.neutral.word}`,
    );
    play.append(element(document, "span", "cb-pc-play-icon"), reading);
    card.append(
      element(document, "div", "cb-pc-tone-head"),
      element(
        document,
        "p",
        "cb-pc-tone-name",
        "Neutral tone, short and unstressed. Its pitch varies with the tone before it.",
      ),
    );
    card.querySelector(".cb-pc-tone-head")?.append(play);
    tones.append(card);
  }

  children.push(tones);
  panel.replaceChildren(...children);
}

/** One part of the formula above the grid: what it says, and what it is. */
interface FormulaPart {
  readonly text: string;
  readonly caption: string;
}

/**
 * How a syllable is put together, as the line above the grid writes it.
 *
 * `zh + uang = zhuang`. A syllable with no initial is written with y or w in
 * front of a final beginning with i, u or ü, and the line shows that letter
 * where the initial would be, so `y + ün = yun`. The rest have nothing there.
 * @param syllable - The syllable chosen.
 * @returns The initial, the final and the syllable.
 */
export function formulaOf(
  syllable: ChartSyllable,
): readonly [FormulaPart, FormulaPart, FormulaPart] {
  const glide =
    /^[yw]/u.test(syllable.pinyin) && /^[iuü]/u.test(syllable.final);
  const front: FormulaPart =
    syllable.initial !== ""
      ? { text: syllable.initial, caption: "initial" }
      : glide
        ? { text: syllable.pinyin.charAt(0), caption: "no initial" }
        : { text: "–", caption: "no initial" };

  return [
    front,
    { text: syllable.final, caption: "final" },
    { text: syllable.pinyin, caption: "syllable" },
  ];
}

/**
 * Write the formula into the line above the grid.
 * @param line - The line, which is replaced.
 * @param syllable - The syllable chosen.
 */
export function drawFormula(line: HTMLElement, syllable: ChartSyllable): void {
  const document = line.ownerDocument;
  const [front, final, whole] = formulaOf(syllable);
  const part = (made: FormulaPart, kind: string): HTMLElement => {
    const box = element(document, "span", `cb-pc-formula-part is-${kind}`);
    const text = element(document, "span", "cb-pc-formula-text", made.text);

    text.lang = "zh-Latn-CN-pinyin";
    box.append(
      text,
      element(document, "span", "cb-pc-formula-caption", made.caption),
    );

    return box;
  };
  const sign = (text: string): HTMLElement =>
    element(document, "span", "cb-pc-formula-sign", text);

  line.replaceChildren(
    part(front, "initial"),
    sign("+"),
    part(final, "final"),
    sign("="),
    part(whole, "whole"),
  );
}
